// The brand marks as PATH DATA — deliberately JSX-free.
//
// WHY A SECOND FILE beside index.tsx. Two lanes want these marks and only one
// of them has a DOM. The Solid components next door render them into the
// chrome; `modules/lib`'s label and card builders render them into PRINTABLE
// STRINGS and are typechecked under the worker tsconfig (no `lib.dom`, no JSX),
// so they cannot import a `.tsx`. Splitting the geometry out means one
// definition serves both instead of a path being re-typed into a print builder
// — which is how a logo quietly drifts from the brand.
//
// Reached from the print lane as `@erp/ui/brand/marks`, an EXACT key in the
// package's exports map: the `./*` pattern beside it resolves only under the
// `solid` condition, which the worker lane does not set.
//
// Every mark here is a SINGLE CLOSED PATH with no stroke and no gradient. That
// is not an aesthetic preference — it is what survives a 203 dpi monochrome
// thermal head, which dithers any grey into a smear and thins any hairline
// stroke below one dot.

/** A mark as the two things an `<svg>` needs and nothing else. */
export interface BrandPath {
  /** Verbatim from the source artwork — never re-origined, so no coordinate is re-typed. */
  viewBox: string;
  /** The `d` of the one path. Fill rule is nonzero (SVG's default) in both sources. */
  d: string;
}

/**
 * The Shippra mark — `../shippra-planning/logos/Shippra/SVG/logofavicon.svg`,
 * the same geometry the portals' `favicon.svg` files carry.
 */
export const SHIPPRA_MARK: BrandPath = {
  viewBox: "0 0 200 200",
  d: "M4.28,119.24v27.21c0,18.35,17.87,33.23,39.91,33.23h31.16s0-50.82,0-50.82h17.65v50.82s42.42,0,42.42,0l17.65-50.82v50.82s13.56,0,13.56,0c16.06,0,29.08-10.84,29.08-24.21v-74.73c0-32.66-31.12-59.27-70.01-60.39-10.17,121.97,33.11,102.44,36.04,104.96-9.14,4.26-15.74,4.19-20.6,2.42-23.53-8.55-24.93-63.67-22.16-107.36-.7.02-1.4.04-2.1.07-30.33,1.31-57.66,12.09-77.79,28.85C17.59,67.18,4.28,91.91,4.28,119.24ZM161.74,58.18c0,4.78-3.88,8.66-8.66,8.66s-8.66-3.88-8.66-8.66,3.87-8.66,8.66-8.66,8.66,3.87,8.66,8.66Z",
};

/**
 * The Outman mark — the interlocking-loops square.
 *
 * PROVENANCE: extracted from the vector layer of `outman-municipal-deck.pdf`
 * (`pdftocairo -svg`), which is where the only machine-readable copy of the
 * mark lives; the deck's OUTMAN wordmark beside it is set in a typeface, not a
 * path, so only the glyph is captured here. The viewBox keeps the PDF's own
 * user-space origin (a 43.640625 mm square starting at 54,45) rather than being
 * translated to 0,0 — an offset viewBox costs nothing and means every number
 * below is the artwork's, unedited.
 */
export const OUTMAN_MARK: BrandPath = {
  viewBox: "54 45 43.640625 43.640625",
  d: "M 86.734375 45 L 82.828125 45 C 81.621094 45 80.644531 45.976562 80.644531 47.183594 L 80.644531 51 C 80.644531 52.203125 79.667969 53.183594 78.460938 53.183594 L 73.1875 53.183594 C 71.984375 53.183594 71.007812 52.203125 71.007812 51 L 71.007812 47.183594 C 71.007812 45.976562 70.027344 45 68.824219 45 L 64.914062 45 C 58.890625 45 54.003906 49.882812 54.003906 55.910156 L 54.003906 77.730469 C 54.003906 83.757812 58.890625 88.640625 64.914062 88.640625 L 68.824219 88.640625 C 70.027344 88.640625 71.007812 87.664062 71.007812 86.457031 L 71.007812 82.640625 C 71.007812 81.4375 71.984375 80.457031 73.1875 80.457031 L 78.460938 80.457031 C 79.667969 80.457031 80.644531 81.4375 80.644531 82.640625 L 80.644531 86.457031 C 80.644531 87.664062 81.621094 88.640625 82.828125 88.640625 L 86.734375 88.640625 C 92.761719 88.640625 97.644531 83.757812 97.644531 77.730469 L 97.644531 55.910156 C 97.644531 49.882812 92.761719 45 86.734375 45 Z M 71.082031 78.277344 C 71.082031 79.480469 70.105469 80.457031 68.902344 80.457031 L 66.28125 80.457031 C 63.871094 80.457031 61.917969 78.503906 61.917969 76.09375 L 61.917969 57.546875 C 61.917969 55.136719 63.871094 53.183594 66.28125 53.183594 L 68.902344 53.183594 C 70.105469 53.183594 71.082031 54.160156 71.082031 55.363281 Z M 89.886719 76.09375 C 89.886719 78.503906 87.933594 80.457031 85.519531 80.457031 L 82.902344 80.457031 C 81.699219 80.457031 80.722656 79.480469 80.722656 78.277344 L 80.722656 55.363281 C 80.722656 54.160156 81.699219 53.183594 82.902344 53.183594 L 85.519531 53.183594 C 87.933594 53.183594 89.886719 55.136719 89.886719 57.546875 Z M 89.886719 76.09375 ",
};

/* ------------------------------- the wordmarks ------------------------------- */
// One spelling each, one place. Print builders and chrome both read these, so a
// label can never disagree with a sidebar about what the product is called.

/** The wordmark the chrome shows next to the Shippra mark. */
export const BRAND_NAME = "SHIPPRA ERP";
/** The Outman wordmark. Uppercase is the mark's own setting, not a CSS choice. */
export const OUTMAN_NAME = "OUTMAN";
/** The domain, as it is written on the deck's colophon. */
export const OUTMAN_DOMAIN = "outman.io";

/**
 * The attribution a scannable artefact carries: Shippra owns the record, Outman
 * does the scanning, and a label says which is which rather than lining both
 * marks up as sponsors.
 */
export const OUTMAN_SCAN_CREDIT = "SCANNING BY OUTMAN.IO";
